import React, { useEffect, useState, useRef } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button, Alert, CircularProgress,
  Chip, Accordion, AccordionSummary, AccordionDetails, Stack, Divider,
  LinearProgress, Tooltip, IconButton, FormControlLabel, Checkbox
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import { swarmApi } from '../api';

const STATUS_COLOR = {
  proposed: 'default',
  awaiting_approval: 'warning',
  executing: 'info',
  completed: 'success',
  failed: 'error',
  rejected: 'default',
  cancelled: 'default',
};

const RISK_COLOR = {
  low: 'success',
  medium: 'warning',
  high: 'error',
};

const STEP_COLOR = {
  pending: 'default',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  skipped: 'default',
};

const EXAMPLES = [
  'List all services and their replica health',
  'Deploy 2 replicas of traefik/whoami named whoami-demo on port 8090',
  'Scale the gateway service to 2 replicas',
  'Show recent logs for measeyswarm_gateway',
  'Prune unused Docker images',
];

function IntentConsole({ user }) {
  const [message, setMessage] = useState('');
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actingId, setActingId] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const inputRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  const fetchIntents = async () => {
    try {
      const res = await swarmApi.listIntents(40);
      setIntents(res.data || []);
      setError('');
    } catch {
      setError('Failed to load intents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntents();
    const t = setInterval(fetchIntents, 15000);
    return () => clearInterval(t);
  }, []);

  const submitIntent = async (text) => {
    const msg = (text || message).trim();
    if (!msg || submitting) return;
    if (!isAdmin) {
      setError('Only admins can submit intents');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const res = await swarmApi.createIntent(msg, { source: 'intent_console' }, { dryRun });
      setMessage('');
      const intent = res.data;
      if (intent.dryRun && intent.status === 'completed') {
        setSuccess('Dry-run completed — no cluster changes were made');
      } else if (intent.status === 'awaiting_approval') {
        setSuccess(`Plan ready — awaiting your approval (risk: ${intent.maxRisk})`);
      } else if (intent.status === 'completed') {
        setSuccess('Intent completed autonomously');
      } else if (intent.status === 'proposed') {
        setSuccess('Shadow mode: plan logged only (not executed). Raise autonomy tier to human_review or autonomous to act.');
      } else if (intent.status === 'failed') {
        setError(intent.errorMessage || 'Intent planning/execution failed');
      } else {
        setSuccess(`Intent created (${intent.status})`);
      }
      await fetchIntents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create intent');
      if (err.response?.data?.intent) {
        await fetchIntents();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const review = async (id, decision) => {
    setActingId(id);
    try {
      await swarmApi.reviewIntent(id, decision);
      setSuccess(decision === 'approved' ? 'Plan approved and executed' : 'Plan rejected');
      await fetchIntents();
    } catch (err) {
      setError(err.response?.data?.message || 'Review failed');
    } finally {
      setActingId(null);
    }
  };

  const cancel = async (id) => {
    setActingId(id);
    try {
      await swarmApi.cancelIntent(id);
      await fetchIntents();
    } catch (err) {
      setError(err.response?.data?.message || 'Cancel failed');
    } finally {
      setActingId(null);
    }
  };

  const formatArgs = (json) => {
    try {
      return JSON.stringify(JSON.parse(json || '{}'), null, 2);
    } catch {
      return json || '{}';
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ChatIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Intent Console</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchIntents}>Refresh</Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe what you want in plain English. The AI proposes a tool plan; high-risk or human-review actions need approval before execution.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          placeholder="e.g. Deploy 2 replicas of nginx:alpine named web-demo on port 8088"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submitIntent();
            }
          }}
          disabled={submitting || !isAdmin}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={submitting || !isAdmin} size="small" />}
              label="Dry-run (simulate only)"
            />
            {EXAMPLES.map((ex) => (
              <Chip
                key={ex}
                size="small"
                label={ex.length > 42 ? `${ex.slice(0, 42)}…` : ex}
                onClick={() => { setMessage(ex); inputRef.current?.focus(); }}
                variant="outlined"
                disabled={submitting || !isAdmin}
              />
            ))}
          </Stack>
          <Button
            variant="contained"
            endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={() => submitIntent()}
            disabled={submitting || !message.trim() || !isAdmin}
          >
            {submitting ? 'Planning…' : dryRun ? 'Simulate' : 'Send'}
          </Button>
        </Box>
        {!isAdmin && (
          <Alert severity="info" sx={{ mt: 1 }}>Viewers can browse plans; only admins can submit and approve.</Alert>
        )}
      </Paper>

      {submitting && <LinearProgress sx={{ mb: 2 }} />}

      <Typography variant="h6" sx={{ mb: 1 }}>Recent intents</Typography>
      {intents.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No intents yet. Try one of the example chips above.</Typography>
        </Paper>
      ) : (
        intents.map((intent) => (
          <Accordion key={intent.id} defaultExpanded={intent.status === 'awaiting_approval'} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                <Typography fontWeight="medium" sx={{ flexGrow: 1 }}>
                  #{intent.id} {intent.summary || intent.message}
                </Typography>
                <Chip size="small" label={intent.status} color={STATUS_COLOR[intent.status] || 'default'} />
                {intent.dryRun && <Chip size="small" label="dry-run" color="info" variant="outlined" />}
                <Chip size="small" label={`risk: ${intent.maxRisk}`} color={RISK_COLOR[intent.maxRisk] || 'default'} variant="outlined" />
                {intent.confidence > 0 && (
                  <Tooltip title="Planner confidence">
                    <Chip size="small" label={`${Math.round(intent.confidence * 100)}%`} variant="outlined" />
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>You:</strong> {intent.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                by {intent.createdBy} · {new Date(intent.createdAt).toLocaleString()}
                {intent.autonomyTier && ` · tier=${intent.autonomyTier}`}
                {intent.reviewedBy && ` · reviewed by ${intent.reviewedBy}`}
              </Typography>
              {intent.errorMessage && (
                <Alert severity="error" sx={{ mb: 1 }}>{intent.errorMessage}</Alert>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Plan steps</Typography>
              {intent.steps?.map((step) => (
                <Paper key={step.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {step.stepOrder}. {step.tool}
                    </Typography>
                    <Chip size="small" label={step.risk} color={RISK_COLOR[step.risk] || 'default'} />
                    <Chip size="small" label={step.status} color={STEP_COLOR[step.status] || 'default'} />
                  </Box>
                  {step.rationale && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{step.rationale}</Typography>
                  )}
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1,
                      fontSize: 11, overflow: 'auto', maxHeight: 120, m: 0
                    }}
                  >
                    {formatArgs(step.argsJson)}
                  </Box>
                  {step.errorMessage && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{step.errorMessage}</Typography>
                  )}
                  {step.resultJson && step.status === 'succeeded' && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 0.5, p: 1, bgcolor: 'success.dark', color: 'success.contrastText',
                        borderRadius: 1, fontSize: 11, overflow: 'auto', maxHeight: 160, m: 0,
                        opacity: 0.9
                      }}
                    >
                      {formatArgs(step.resultJson)}
                    </Box>
                  )}
                </Paper>
              ))}

              {isAdmin && (intent.status === 'awaiting_approval' || intent.status === 'proposed') && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {intent.status === 'awaiting_approval' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUpIcon />}
                        disabled={actingId === intent.id}
                        onClick={() => review(intent.id, 'approved')}
                      >
                        Approve & execute
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<ThumbDownIcon />}
                        disabled={actingId === intent.id}
                        onClick={() => review(intent.id, 'rejected')}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {intent.status === 'proposed' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<ThumbUpIcon />}
                      disabled={actingId === intent.id}
                      onClick={() => review(intent.id, 'approved')}
                    >
                      Execute anyway (break-glass)
                    </Button>
                  )}
                  <IconButton size="small" onClick={() => cancel(intent.id)} disabled={actingId === intent.id} title="Cancel">
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Container>
  );
}

export default IntentConsole;
